import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Building2, Clock, ChevronRight, Users, Briefcase, Pause, XCircle, Edit, Loader2
} from "lucide-react";
import { useJobPostDetail } from "@/hooks/useJobPosts";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { usePartnerContext } from "@/hooks/usePartnerContext";
import { format } from "date-fns";

type VagaStatus = "publicada" | "em_revisao" | "rascunho" | "pausada" | "encerrada";

const statusConfig: Record<VagaStatus, { label: string; color: string }> = {
  publicada: { label: "Publicada", color: "bg-green-100 text-green-800" },
  em_revisao: { label: "Em revisão", color: "bg-yellow-100 text-yellow-800" },
  rascunho: { label: "Rascunho", color: "bg-gray-100 text-gray-600" },
  pausada: { label: "Pausada", color: "bg-orange-100 text-orange-800" },
  encerrada: { label: "Encerrada", color: "bg-red-100 text-red-800" },
};

const EMPLOYMENT_LABELS: Record<string, string> = {
  estagio: "Estágio", aprendiz: "Aprendiz", clt: "CLT", pj: "PJ", freelancer: "Freelancer",
};
const WORK_MODEL_LABELS: Record<string, string> = {
  presencial: "Presencial", remoto: "Remoto", hibrido: "Híbrido",
};

export default function EmpresaVagaDetalhe() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { orgId } = usePartnerContext();

  const { data: job, isLoading: jobLoading } = useJobPostDetail(id);

  // Fetch applications for this job
  const { data: applications, isLoading: appsLoading } = useQuery({
    queryKey: ["job_applications_for_post", id],
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("job_applications")
        .select("id, status, created_at, user_id, profiles(full_name)")
        .eq("job_id", id!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  // Count contact requests for this job
  const { data: contactCount } = useQuery({
    queryKey: ["contact_count_for_job", id],
    enabled: !!id,
    queryFn: async () => {
      const { count, error } = await supabase
        .from("contact_requests")
        .select("id", { count: "exact", head: true })
        .eq("job_id", id!);
      if (error) throw error;
      return count ?? 0;
    },
  });

  if (jobLoading || appsLoading) {
    return (
      <div className="page-container-desktop flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!job) {
    return (
      <div className="page-container-desktop text-center py-20">
        <p className="text-muted-foreground">Vaga não encontrada.</p>
        <Button variant="ghost" className="mt-4" onClick={() => navigate("/empresa/vagas")}>
          <ArrowLeft className="w-4 h-4 mr-2" />Voltar
        </Button>
      </div>
    );
  }

  const status = (job.status ?? "rascunho") as VagaStatus;
  const requiredSkills = (job.required_skills as string[] | null) ?? [];
  const niceSkills = (job.nice_skills as string[] | null) ?? [];
  const responsibilities = (job.responsibilities as string[] | null) ?? [];
  const requirementsMust = (job.requirements_must as string[] | null) ?? [];
  const apps = applications ?? [];
  const location = [job.location_city, job.location_state].filter(Boolean).join(", ");

  return (
    <div className="page-container-desktop max-w-4xl">
      {/* Header */}
      <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate("/empresa/vagas")}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <span className="text-sm text-muted-foreground">Minhas Vagas / Detalhe</span>
      </motion.div>

      {/* Main Info */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-card rounded-2xl p-6 shadow-card border border-border mb-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
              <Building2 className="w-7 h-7 text-primary" />
            </div>
            <div>
              <div className="flex items-center gap-3 mb-1">
                <h1 className="font-heading text-xl font-bold text-foreground">{job.title}</h1>
                <span className={`text-xs px-2 py-1 rounded-full ${statusConfig[status]?.color ?? "bg-gray-100 text-gray-600"}`}>
                  {statusConfig[status]?.label ?? status}
                </span>
              </div>
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <Badge variant="secondary">{EMPLOYMENT_LABELS[job.employment_type] ?? job.employment_type}</Badge>
                {(job.work_model || location) && (
                  <span>{[WORK_MODEL_LABELS[job.work_model ?? ""] ?? job.work_model, location].filter(Boolean).join(" • ")}</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {job.description && <p className="text-sm text-muted-foreground mb-4">{job.description}</p>}

        {requirementsMust.length > 0 && (
          <div className="mb-3">
            <p className="text-xs font-medium text-foreground mb-1.5">Requisitos obrigatórios</p>
            <ul className="list-disc list-inside text-sm text-muted-foreground space-y-0.5">
              {requirementsMust.map((r, i) => <li key={i}>{r}</li>)}
            </ul>
          </div>
        )}

        {/* Skills */}
        {requiredSkills.length > 0 && (
          <div className="mb-3">
            <p className="text-xs font-medium text-foreground mb-1.5">Habilidades obrigatórias</p>
            <div className="flex flex-wrap gap-1.5">
              {requiredSkills.map(s => <Badge key={s} variant="default" className="text-xs">{s}</Badge>)}
            </div>
          </div>
        )}
        {niceSkills.length > 0 && (
          <div className="mb-4">
            <p className="text-xs font-medium text-foreground mb-1.5">Habilidades desejáveis</p>
            <div className="flex flex-wrap gap-1.5">
              {niceSkills.map(s => <Badge key={s} variant="outline" className="text-xs">{s}</Badge>)}
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate(`/empresa/vagas/${id}/editar`)}>
            <Edit className="w-4 h-4" /> Editar
          </Button>
          {status === "publicada" && (
            <Button variant="outline" size="sm"><Pause className="w-4 h-4" /> Pausar</Button>
          )}
          {status !== "encerrada" && (
            <Button variant="ghost" size="sm" className="text-destructive"><XCircle className="w-4 h-4" /> Encerrar</Button>
          )}
        </div>
      </motion.div>

      {/* Metrics */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-card rounded-xl p-4 border border-border text-center">
          <Users className="w-5 h-5 text-primary mx-auto mb-2" />
          <p className="text-2xl font-bold text-foreground">{apps.length}</p>
          <p className="text-xs text-muted-foreground">Candidaturas</p>
        </div>
        <div className="bg-card rounded-xl p-4 border border-border text-center">
          <Clock className="w-5 h-5 text-primary mx-auto mb-2" />
          <p className="text-2xl font-bold text-foreground">{contactCount ?? 0}</p>
          <p className="text-xs text-muted-foreground">Contatos</p>
        </div>
        <div className="bg-card rounded-xl p-4 border border-border text-center">
          <Briefcase className="w-5 h-5 text-primary mx-auto mb-2" />
          <p className="text-2xl font-bold text-foreground">
            {job.deadline_date ? format(new Date(job.deadline_date), "dd/MM") : "—"}
          </p>
          <p className="text-xs text-muted-foreground">Prazo</p>
        </div>
      </motion.div>

      {/* Candidaturas */}
      <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-lg font-semibold text-foreground">Candidaturas ({apps.length})</h2>
          {apps.length > 0 && (
            <Button variant="link" size="sm" onClick={() => navigate(`/empresa/vagas/${id}/candidaturas`)}>
              Ver todos <ChevronRight className="w-4 h-4" />
            </Button>
          )}
        </div>

        {apps.length === 0 ? (
          <div className="bg-muted/50 rounded-2xl p-12 text-center">
            <Users className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">Nenhuma candidatura ainda.</p>
          </div>
        ) : (
          <div className="bg-card rounded-2xl border border-border overflow-hidden">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left text-xs font-medium text-muted-foreground p-4">Candidato</th>
                  <th className="text-left text-xs font-medium text-muted-foreground p-4">Status</th>
                  <th className="text-left text-xs font-medium text-muted-foreground p-4">Data</th>
                  <th className="text-right text-xs font-medium text-muted-foreground p-4">Ações</th>
                </tr>
              </thead>
              <tbody>
                {apps.slice(0, 5).map((app, i) => (
                  <tr key={app.id} className={i < Math.min(apps.length, 5) - 1 ? "border-b border-border" : ""}>
                    <td className="p-4">
                      <span className="font-medium text-foreground">
                        {(app.profiles as any)?.full_name ?? "Sem nome"}
                      </span>
                    </td>
                    <td className="p-4"><Badge variant="secondary" className="text-xs capitalize">{app.status}</Badge></td>
                    <td className="p-4 text-sm text-muted-foreground">{format(new Date(app.created_at), "dd/MM/yyyy")}</td>
                    <td className="p-4 text-right">
                      <Button variant="ghost" size="sm" onClick={() => navigate(`/empresa/vagas/${id}/candidaturas/${app.id}`)}>Pipeline</Button>
                      <Button variant="ghost" size="sm" onClick={() => navigate(`/empresa/talento/${app.user_id}`)}>Perfil</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </motion.section>
    </div>
  );
}
